import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import LanguageDropdown from '../components/LanguageDropdown';
import Error from '../components/Error';
import Loading from '../components/Loading';
import TreeBox from '../components/TreeBox';
import pageStyles from '../styles/page-common.module.css';
import { getWeekIndex } from '../utils';
import { getTreeMetadata, getLatestTreeID } from '../backend';


function Home() {
  const user = useSelector((state) => state.user);
  const streakState = useSelector((state) => state.streak);
  const {recordsLoaded, ...treeRecords} = useSelector((state) => state.treeRecords);

  const [continueTreeID, setContinueTreeID] = useState(null);
  const [continueTreeMetadata, setContinueTreeMetadata] = useState(null);
  const [continueTreeChecked, setContinueTreeChecked] = useState(false);
  const [weekTreeMetadata, setWeekTreeMetadata] = useState(null);  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // if user login state changes, reload everything
  useEffect(() => {
    setContinueTreeID(null);
    setContinueTreeMetadata(null);
    setContinueTreeChecked(false);
    setWeekTreeMetadata(null);
    setError(null);
    setLoading(true);
  }, [user])

  // when page loads, check to see if there is a tree to continue
  useEffect(() => {
    if (loading && !continueTreeChecked) {
      const recs = Object.entries(treeRecords)
      .filter(([key, treeRecord]) => !treeRecord.complete)
      .map(([key, treeRecord]) => {
        return { key, ...treeRecord };
      })
      if (recs.length > 0) {
        const mostRecentRecID = recs.reduce((latest, current) => {
          return new Date(current.lastReadDate) > new Date(latest.lastReadDate)
            ? current
            : latest;
        }).key;
        setContinueTreeID(mostRecentRecID);
      }
      setContinueTreeChecked(true);
    }
  }, [loading, continueTreeChecked, treeRecords])

  // when continue tree checked, load all needed tree metadata
  useEffect(() => {
    async function loadTrees() {
      try {
        // query for tree metadata
        const ids = [
          await getLatestTreeID({ language: user.currentLanguage, level: 'beg', latestWeek: getWeekIndex() }),
          await getLatestTreeID({ language: user.currentLanguage, level: 'int', latestWeek: getWeekIndex() }),
          await getLatestTreeID({ language: user.currentLanguage, level: 'adv', latestWeek: getWeekIndex() }),
        ]
        const response = await getTreeMetadata({
          ids: ids,
        });


        // order trees by level
        const levels = ["beg", "int", "adv"];
        const orderedTrees = [];
        levels.forEach(level => orderedTrees.push(response.find(tree => tree._id.endsWith(`${level}`))));
        setWeekTreeMetadata(orderedTrees);

        // if there is a tree to continue, query for that
        if (continueTreeID) {
          const response = await getTreeMetadata({
            ids: [continueTreeID],
          });
          setContinueTreeMetadata(response[0])
        }
      }
      catch(err) {
        if (err.response && err.response.status=== 404) {
          setError("Latest trees not found.")
          setLoading(false);
        }
        else {
          setError("Some error occurred.")
          setLoading(false);
        }
      }
    }
    if (loading && continueTreeChecked) {
      loadTrees();
    }
  }, [loading, continueTreeID, continueTreeChecked, user, treeRecords]);

  // if weekTreeMetadata not null and continueTreeMetadata not null then set loading to false
  useEffect(() => {
    if (loading && weekTreeMetadata) {
      if (continueTreeChecked && continueTreeID && continueTreeMetadata) {
        setLoading(false);
      } else if (continueTreeChecked && !continueTreeID) {
        setLoading(false);
      }
    }
  }, [loading, weekTreeMetadata, continueTreeMetadata, continueTreeID, continueTreeChecked]);

  if (error) return <Error/>;
  if (loading) return <Loading/>;

  return (
    <>
      <Helmet>
        <title>Home | LingoLeaf</title>
      </Helmet>
      <div className={pageStyles.styledPage}>
        {user.isLoggedIn ? <h1>Welcome, {user.username}!</h1> : <h1>Welcome to LingoLeaf!</h1>}
        {user.isLoggedIn && streakState.pending && streakState.streak !== 0 && (
          <div className={pageStyles.alertBanner}>
            <p>You have not yet collected a tree this week. To maintain your <NavLink to='/streak'>weekly streak</NavLink>, complete a tree before the end of Sunday!</p>
          </div>
        )}
        {
          !user.isLoggedIn &&
          <>
            <ul>
              <li style={{ textAlign: 'left' }}>Learn a new language by reading "trees" (short stories with annotations, translations, and comprehension questions)</li>
              <li style={{ textAlign: 'left' }}>New stories released regularly, with many supported <NavLink to='/languages'><strong>languages</strong></NavLink> and three difficulty levels.</li>
              <li style={{ textAlign: 'left' }}>Find out more on our <NavLink to='/faqs'><strong>FAQs</strong></NavLink> page</li>
              <li style={{ textAlign: 'left' }}>No signup/login necessary, but with a user account you can save your progress and access additional features.</li>
              <li style={{ textAlign: 'left' }}>Completely free, but any <NavLink to='/support-us'><strong>support</strong></NavLink> you could give us would be greatly appreciated.</li>
            </ul>
            <br/>
          </>
        }
        <hr className={pageStyles.hrule} />
        {error 
          ?
          <div className={pageStyles.alertBanner}><p>{error}</p></div>
          :
          <>
            {continueTreeID && (
              <>
                <h2>Continue Reading</h2>
                <TreeBox treeMetadata={continueTreeMetadata} />
                <hr className={pageStyles.hrule} />
              </>
            )}  
            <h2>Latest Trees</h2>
            <p>Language:</p>
            <LanguageDropdown className={pageStyles.dropdown}/>
            <TreeBox treeMetadata={weekTreeMetadata[0]} />
            <TreeBox treeMetadata={weekTreeMetadata[1]} />
            <TreeBox treeMetadata={weekTreeMetadata[2]} />
          </>
        }
        <hr className={pageStyles.hrule} />
        <p>Alternatively, visit your list of <NavLink to='/in-progress'>trees in progress</NavLink> or the <NavLink to='/archive'>tree archive</NavLink>.</p>
      </div>
    </>
  );
}

export default Home;